import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import dayjs from "dayjs";

import {
  getAllNotifications,
  deleteNotification,
  getAppMaintenanceStatus,
  disableAppMaintenance,
} from "../../../../api/system/notifications";

import customSelectStyles from "../../../../components/ui/customSelectStyles";
import Pagination from "../../../../components/common/Pagination";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../../utils/confirmDelete";
import { canBypassAppMaintenance } from "../../../../utils/auth";

import NotificationCreateModal from "./NotificationCreateModal";
import NotificationViewModal from "./NotificationViewModal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotateRight, faEye, faTrash, faUnlock } from "@fortawesome/free-solid-svg-icons";

export default function NotificationsListPage() {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    module_code: "",
    sender_id: "",
    page: 1,
    limit: 10,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [maintenance, setMaintenance] = useState({ enabled: false });
  const [reopening, setReopening] = useState(false);

  const [openCreate, setOpenCreate] = useState(false);
  const [openView, setOpenView] = useState(null);

  const moduleOptions = [
    { value: "", label: "Tất cả" },
    { value: "system", label: "Hệ thống" },
    { value: "hrm", label: "HRM" },
    { value: "crm", label: "CRM" },
    { value: "finance", label: "Tài chính" },
    { value: "elearning", label: "E-learning" },
  ];

  const loadMaintenance = useCallback(async () => {
    const status = await getAppMaintenanceStatus();
    setMaintenance(status || { enabled: false });
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getAllNotifications({
        keyword: filters.keyword,
        module_code: filters.module_code,
        sender_id: filters.sender_id,
        page: filters.page,
        limit: filters.limit,
      });

      setData(res.data || []);
      setTotalRecords(res.total || 0);
    } catch (err) {
      showErrorToast("Không thể tải danh sách thông báo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters.keyword, filters.module_code, filters.page, filters.limit]);

  useEffect(() => {
    loadMaintenance();
    const onUpdated = () => loadMaintenance();
    window.addEventListener("app-maintenance-updated", onUpdated);
    return () => window.removeEventListener("app-maintenance-updated", onUpdated);
  }, [loadMaintenance]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [key]: value,
    }));
  };

  const handleDelete = async (row) => {
    const id = typeof row === "object" ? row?.id : row;
    const cf = await confirmDelete({
      type: DELETE_ENTITY.notification,
      name: typeof row === "object" ? row?.title : undefined,
    });

    if (!cf.isConfirmed) return;

    try {
      await deleteNotification(id);
      showSuccessToast("Đã xóa thành công!");
      loadData();
    } catch (err) {
      showErrorToast("Không thể xóa thông báo");
    }
  };

  const handleReopenApp = async () => {
    const cf = await showConfirmDialog({
      title: "Mở lại ứng dụng?",
      text: "Người dùng sẽ truy cập lại được hệ thống ngay sau khi xác nhận.",
      confirmText: "Mở lại app",
      cancelText: "Hủy",
    });
    if (!cf.isConfirmed) return;

    try {
      setReopening(true);
      await disableAppMaintenance();
      setMaintenance({ enabled: false });
      window.dispatchEvent(
        new CustomEvent("app-maintenance-updated", { detail: { enabled: false } })
      );
      showSuccessToast("Đã mở lại ứng dụng");
    } catch (err) {
      showErrorToast(err?.message || "Không thể mở lại ứng dụng");
    } finally {
      setReopening(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Quản lý thông báo hệ thống</h1>

        <div className="flex items-center gap-3">
          <button
            onClick={() => loadData()}
            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faRotateRight} />
            Làm mới
          </button>

          <button
            onClick={() => setOpenCreate(true)}
            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Thêm thông báo
          </button>
        </div>
      </div>

      {maintenance.enabled && canBypassAppMaintenance() && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">App đang tắt tạm thời</p>
            <p className="text-xs text-amber-800 mt-0.5 line-clamp-2">
              {maintenance.title || "Hệ thống đang bảo trì"}
              {maintenance.message ? ` — ${maintenance.message}` : ""}
            </p>
          </div>
          <button
            type="button"
            disabled={reopening}
            onClick={handleReopenApp}
            className="px-3 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faUnlock} />
            {reopening ? "Đang mở lại…" : "Mở lại app"}
          </button>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow mb-4 flex flex-wrap items-center gap-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Tìm theo tiêu đề / nội dung"
            value={filters.keyword}
            onChange={(e) => updateFilter("keyword", e.target.value)}
            className="border rounded px-3 py-2 w-72"
          />
        </div>

        <div className="w-60">
          <Select
            options={moduleOptions}
            styles={customSelectStyles}
            value={moduleOptions.find((o) => o.value === filters.module_code)}
            onChange={(opt) => updateFilter("module_code", opt.value)}
            placeholder="Chọn module"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <table className="w-full table-auto text-left">
          <thead className="bg-gray-100 text-gray-500 text-sm">
            <tr>
              <th className="p-3 w-14 text-center">#</th>
              <th className="p-3">Title & Message</th>
              <th className="p-3 min-w-32">Module Code</th>
              <th className="p-3 min-w-48">Created By</th>
              <th className="p-3 min-w-40">Created At</th>
              <th className="p-3 min-w-24 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="p-6 text-center text-gray-500">
                  Đang tải...
                </td>
              </tr>
            )}

            {!loading && data.length === 0 && (
              <tr>
                <td colSpan="6" className="p-6 text-center text-gray-400 italic">
                  Không có dữ liệu
                </td>
              </tr>
            )}

            {!loading &&
              data.map((item, index) => {
                const stt = (filters.page - 1) * filters.limit + index + 1;

                return (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="p-3 text-center">{stt}</td>

                    <td className="p-3">
                      <span className="font-medium">{item.title}</span>
                      <div className="text-gray-600 text-sm line-clamp-1">{item.message}</div>
                    </td>

                    <td className="p-3">
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                        {item.module_code || "N/A"}
                      </span>
                    </td>

                    <td className="p-3">{item.sender_name}</td>

                    <td className="p-3 text-sm text-gray-700">
                      {dayjs(item.created_at).format("HH:mm DD-MMM-YYYY")}
                    </td>

                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => setOpenView(item)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Xem"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>

                        <button
                          onClick={() => handleDelete(item)}
                          className="text-red-600 hover:text-red-800"
                          title="Xóa"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <Pagination
          totalRecords={totalRecords}
          currentPage={filters.page}
          pageSize={filters.limit}
          onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
          onPageSizeChange={(l) => setFilters((prev) => ({ ...prev, page: 1, limit: l }))}
        />
      </div>

      {openCreate && (
        <NotificationCreateModal
          onClose={() => setOpenCreate(false)}
          onSuccess={() => {
            loadData();
            loadMaintenance();
          }}
        />
      )}

      {openView && (
        <NotificationViewModal
          item={openView}
          onClose={() => setOpenView(null)}
          onDeleted={() => loadData()}
        />
      )}
    </div>
  );
}
